import { marked } from 'marked'
import katex from 'katex'
import { getCitationFilePath } from '../../api'
import { CitationMetadata } from '../../api/models'

type HtmlParsedAnswer = {
    answerHtml: string
    citations: string[]
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

// Private-use sentinels wrap math and citation placeholders. They cannot appear
// in model output, are never HTML-escaped by marked, and survive the block-level
// whitespace trimming marked applies (e.g. when a placeholder is a whole line).
const MATH_OPEN = ''
const MATH_CLOSE = ''
const CITE_OPEN = ''
const CITE_CLOSE = ''
const MATH_TOKEN_RE = new RegExp(`${MATH_OPEN}(\\d+)${MATH_CLOSE}`, 'g')
const CITE_TOKEN_RE = new RegExp(`${CITE_OPEN}(\\d+)${CITE_CLOSE}`, 'g')

// LaTeX delimiters in priority order: display math must be matched before
// inline so `$$...$$` is not chopped up by the single-`$` rule. Each pattern
// captures the inner TeX in group 1 and only matches a *complete* delimiter
// pair, so an unterminated opener mid-stream is left as plain text.
const MATH_PATTERNS: { regex: RegExp; displayMode: boolean }[] = [
    { regex: /\$\$([\s\S]+?)\$\$/g, displayMode: true },
    { regex: /\\\[([\s\S]+?)\\\]/g, displayMode: true },
    { regex: /\\\(([\s\S]+?)\\\)/g, displayMode: false },
    // Inline `$...$`: guard against currency by requiring no whitespace just
    // inside the delimiters and no digit immediately after the closing `$`.
    { regex: /(?<![\\$])\$(?!\s)([^$\n]+?)(?<!\s)\$(?!\d)/g, displayMode: false },
]

/**
 * Replace LaTeX math spans with sentinel tokens and return the rendered
 * (trusted) KaTeX HTML keyed by token index. Extracting math first means LaTeX
 * display delimiters `\[ ... \]` never reach the citation or markdown parsers.
 */
function extractMath(text: string): { text: string; rendered: string[] } {
    const rendered: string[] = []
    let out = text

    for (const { regex, displayMode } of MATH_PATTERNS) {
        out = out.replace(regex, (match, tex: string) => {
            let html: string
            try {
                html = katex.renderToString(tex.trim(), {
                    displayMode,
                    throwOnError: false,
                    output: 'html',
                })
            } catch {
                // Leave the original delimited text untouched on failure.
                return match
            }
            const token = `${MATH_OPEN}${rendered.length}${MATH_CLOSE}`
            rendered.push(html)
            return token
        })
    }

    return { text: out, rendered }
}

export function parseAnswerToHtml(
    answer: string,
    isStreaming: boolean,
    supContainerClassName: string,
    citationLookup: { [key: string]: CitationMetadata } = {}
): HtmlParsedAnswer {
    const citations: string[] = []

    // trim any whitespace from the end of the answer after removing follow-up questions
    let parsedAnswer = answer.trim()

    // Omit a citation that is still being typed during streaming
    if (isStreaming) {
        let lastIndex = parsedAnswer.length
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === ']') {
                break
            } else if (parsedAnswer[i] === '[') {
                lastIndex = i
                break
            }
        }
        const truncatedAnswer = parsedAnswer.substring(0, lastIndex)
        parsedAnswer = truncatedAnswer
    }

    // 1. Render math to sentinel tokens up front so equations are never mistaken
    //    for citations or markdown and any `<`/`>` inside them is preserved.
    const { text: withMathTokens, rendered: mathHtml } =
        extractMath(parsedAnswer)

    // 2. Replace `[id]` with a citation token only when `id` is a known citation
    //    ID. Anything else (references, array notation) is left for marked to
    //    render as literal text.
    const citationHtml: string[] = []
    const withCitationTokens = withMathTokens.replace(
        /\[([^\]]+)\]/g,
        (match, id: string) => {
            if (!Object.prototype.hasOwnProperty.call(citationLookup, id)) {
                return match
            }

            let citationIndex: number
            if (citations.indexOf(id) !== -1) {
                citationIndex = citations.indexOf(id) + 1
            } else {
                citations.push(id)
                citationIndex = citations.length
            }

            const metadata = citationLookup[id]
            const displayText = metadata?.display || id
            const path = metadata?.path
                ? getCitationFilePath(metadata.path)
                : getCitationFilePath(id)

            const token = `${CITE_OPEN}${citationHtml.length}${CITE_CLOSE}`
            citationHtml.push(
                `<button type="button" class="${escapeHtml(supContainerClassName)}" data-citation-path="${escapeHtml(path)}" title="${escapeHtml(displayText)}"><sup>${citationIndex}</sup></button>`
            )
            return token
        }
    )

    // 3. Render markdown (headings, lists, bold, tables, …). marked escapes all
    //    literal text; our sentinel tokens are plain text and pass through.
    const markedHtml = marked.parse(withCitationTokens, {
        gfm: true,
        breaks: true,
    }) as string

    // 4. Swap sentinel tokens back for their trusted KaTeX / citation markup.
    const answerHtml = markedHtml
        .replace(MATH_TOKEN_RE, (_m, i: string) => mathHtml[Number(i)] ?? '')
        .replace(
            CITE_TOKEN_RE,
            (_m, i: string) => citationHtml[Number(i)] ?? ''
        )

    return {
        answerHtml,
        citations,
    }
}
